import { useState } from "react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow } from "@/components/Eyebrow";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const YEARS = ["2026", "2027", "2028", "2029", "Not sure yet"];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    current_college: "",
    intended_year: "",
    interested_tier: "",
    message: "",
    website: "", // honeypot
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Invalid email";
    if (!form.current_college.trim()) e.current_college = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/leads", {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        current_college: form.current_college,
        intended_year: form.intended_year || null,
        interested_tier: form.interested_tier || null,
        message: form.message || null,
        source: "contact_form",
        website: form.website,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <SEO
        title="Contact Beyond Valencia — Florida Transfer Strategy Consult"
        description="Reach out about a Florida transfer plan, an audit, or Platform, Florida Transfer, and Selective & Elite engagements. Response within 2 business days."
        canonical="/contact"
      />
      <section className="border-b border-stone/30 bg-bone" data-testid="contact-page">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-20 lg:pt-28 pb-16 lg:pb-20">
          <Eyebrow>Contact</Eyebrow>
          <h1 className="font-serif font-medium text-4xl sm:text-5xl lg:text-[4.5rem] text-ink leading-[1.04] tracking-tightest mt-5">
            Tell us what's<br />
            <span className="italic text-ivy">actually next.</span>
          </h1>
          <p className="mt-7 text-ink-soft text-base sm:text-lg max-w-2xl leading-relaxed">
            Most students don't know the difference between a transfer goal and a transfer plan when
            they reach us. That's fine. Send a few lines about where you are and where you're aiming —
            we'll respond within 2 business days.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
        {submitted ? (
          <div className="border border-ivy/40 bg-bone-dim p-10 text-center" data-testid="contact-thanks">
            <CheckCircle2 className="h-10 w-10 text-ivy mx-auto" />
            <h2 className="font-serif font-medium text-3xl text-ink mt-5 tracking-tight">Thanks — we'll be in touch.</h2>
            <p className="text-ink-soft mt-3 text-sm max-w-md mx-auto">
              We'll respond within 2 business days. If it's urgent, mention so in a follow-up.
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-6" data-testid="contact-form" noValidate>
            {/* Honeypot */}
            <div className="hidden">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={onChange("website")}
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[11px] uppercase tracking-[0.22em] text-stone">Name *</Label>
                <Input id="name" data-testid="contact-name" className="rounded-none border-stone/50 bg-bone focus-visible:border-ivy focus-visible:ring-0" value={form.name} onChange={onChange("name")} required />
                {errors.name && <p className="text-xs text-crimson" data-testid="error-name">{errors.name}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[11px] uppercase tracking-[0.22em] text-stone">Email *</Label>
                <Input id="email" type="email" data-testid="contact-email" className="rounded-none border-stone/50 bg-bone focus-visible:border-ivy focus-visible:ring-0" value={form.email} onChange={onChange("email")} required />
                {errors.email && <p className="text-xs text-crimson" data-testid="error-email">{errors.email}</p>}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-[11px] uppercase tracking-[0.22em] text-stone">Phone (optional)</Label>
                <Input id="phone" data-testid="contact-phone" className="rounded-none border-stone/50 bg-bone focus-visible:border-ivy focus-visible:ring-0" value={form.phone} onChange={onChange("phone")} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="current_college" className="text-[11px] uppercase tracking-[0.22em] text-stone">Current college *</Label>
                <Input id="current_college" data-testid="contact-college" className="rounded-none border-stone/50 bg-bone focus-visible:border-ivy focus-visible:ring-0" value={form.current_college} onChange={onChange("current_college")} required />
                {errors.current_college && <p className="text-xs text-crimson" data-testid="error-current-college">{errors.current_college}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="intended_year" className="text-[11px] uppercase tracking-[0.22em] text-stone">Intended transfer year</Label>
              <Select value={form.intended_year} onValueChange={(v) => setForm({ ...form, intended_year: v })}>
                <SelectTrigger id="intended_year" data-testid="contact-year" className="rounded-none border-stone/50 bg-bone focus:ring-0">
                  <SelectValue placeholder="Choose a year" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {YEARS.map((y) => (
                    <SelectItem key={y} value={y} className="rounded-none">{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-[11px] uppercase tracking-[0.22em] text-stone">Interested tier</Label>
              <RadioGroup
                value={form.interested_tier}
                onValueChange={(v) => setForm({ ...form, interested_tier: v })}
                className="grid sm:grid-cols-4 gap-0 border-t border-l border-stone/40"
              >
                {[
                  { v: "platform",   l: "Platform" },
                  { v: "state",      l: "State" },
                  { v: "nationwide", l: "Nationwide" },
                  { v: "unsure",     l: "Not sure" },
                ].map((opt) => (
                  <Label
                    key={opt.v}
                    htmlFor={`tier-${opt.v}`}
                    className="flex items-center gap-2 border-r border-b border-stone/40 px-4 py-3 cursor-pointer hover:bg-bone-dim transition-colors"
                  >
                    <RadioGroupItem id={`tier-${opt.v}`} value={opt.v} data-testid={`contact-tier-${opt.v}`} className="rounded-none" />
                    <span className="text-sm font-medium">{opt.l}</span>
                  </Label>
                ))}
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="text-[11px] uppercase tracking-[0.22em] text-stone">Anything else?</Label>
              <Textarea
                id="message"
                rows={5}
                data-testid="contact-message"
                className="rounded-none border-stone/50 bg-bone focus-visible:border-ivy focus-visible:ring-0"
                value={form.message}
                onChange={onChange("message")}
                placeholder="Where you are, where you're aiming, what's getting in the way."
              />
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="bg-ivy hover:bg-ivy-light text-bone uppercase tracking-[0.18em] text-xs font-semibold py-4 px-8 transition-colors disabled:opacity-60"
                data-testid="contact-submit"
              >
                {submitting ? "Sending…" : "Send the note"}
              </button>
            </div>
          </form>
        )}
      </section>
    </Layout>
  );
}
